import { TotalResilienceActionsInsight } from '@hazadapt-git/public-core-base'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { customDarkBlueColor } from '../../lib/styles/universal'
import { Popover } from '../atoms'
import { topAlignedPopoverPositioning } from '../../lib/entities/misc'
import { IoInformationCircle } from 'react-icons/io5'
import { snakeCaseToTitleCase } from '../../lib/utils'

type UserViewBreakdownMetadata =
    TotalResilienceActionsInsight['user_view_resilience_action_breakdown_metadata']

type ResilienceActionBreakdowns = {
    userViewBreakdown: TotalResilienceActionsInsight['user_view_resilience_action_breakdown']
    userViewMetadata: UserViewBreakdownMetadata
    userViewNote: TotalResilienceActionsInsight['user_view_resilience_action_breakdown_note']
    internalBreakdown?: TotalResilienceActionsInsight['resilience_action_breakdown']
    internalNote: TotalResilienceActionsInsight['resilience_action_breakdown_note']
}

const getResilienceActionBreakdowns = (
    insight: TotalResilienceActionsInsight
): ResilienceActionBreakdowns => ({
    userViewBreakdown:
        insight.user_view_resilience_action_breakdown ??
        insight.resilience_action_breakdown,
    userViewMetadata: insight.user_view_resilience_action_breakdown_metadata,
    userViewNote: insight.user_view_resilience_action_breakdown_note,
    internalBreakdown: insight.resilience_action_breakdown_note
        ? insight.resilience_action_breakdown
        : undefined,
    internalNote: insight.resilience_action_breakdown_note,
})

const renderBreakdownList = (
    breakdown: Record<string, number>,
    metadata?: UserViewBreakdownMetadata
) => (
    <Box
        component="ul"
        m={0}
        pl={'1.25rem'}
        maxHeight={'16rem'}
        overflow={'auto'}
    >
        {Object.entries(breakdown).map(([statName, statValue]) => (
            <Box component="li" key={statName}>
                <Typography component="span" fontWeight={500}>
                    {metadata?.[statName]?.label ??
                        snakeCaseToTitleCase(statName)}
                </Typography>
                :{' '}
                <Typography component="span">
                    {statValue.toLocaleString()}
                </Typography>
                {metadata?.[statName]?.description && (
                    <Typography component="p" variant="body2">
                        {metadata[statName].description}
                    </Typography>
                )}
            </Box>
        ))}
    </Box>
)

const renderHelpText = (insight: TotalResilienceActionsInsight) => {
    const {
        userViewBreakdown,
        userViewMetadata,
        userViewNote,
        internalBreakdown,
        internalNote,
    } = getResilienceActionBreakdowns(insight)
    const showInternalComparison =
        !!internalBreakdown && !!internalNote && !!userViewNote

    if (!userViewBreakdown && !internalBreakdown) {
        return insight.breakdown_subline
    }

    return (
        <>
            <Typography
                component="p"
                fontWeight={600}
                fontSize={'1.25rem'}
                mb="0.25rem"
            >
                Resilience Action Breakdown
            </Typography>
            {insight.breakdown_subline && (
                <Typography component="p" mb="0.75rem" fontStyle="italic">
                    {insight.breakdown_subline}
                </Typography>
            )}
            {showInternalComparison && (
                <Box mb="1rem">
                    <Typography
                        component="p"
                        fontWeight={600}
                        mt="0.75rem"
                        mb="0.25rem"
                    >
                        Internal Breakdown
                    </Typography>
                    {internalNote && (
                        <Typography component="p" mb="0.5rem">
                            {internalNote}
                        </Typography>
                    )}
                    {renderBreakdownList(internalBreakdown)}
                </Box>
            )}
            {userViewBreakdown && (
                <>
                    {showInternalComparison && (
                        <>
                            <Typography
                                component="p"
                                fontWeight={600}
                                mb="0.25rem"
                            >
                                User View
                            </Typography>
                            <Typography component="p" mb="0.5rem">
                                {userViewNote}
                            </Typography>
                        </>
                    )}
                    {renderBreakdownList(userViewBreakdown, userViewMetadata)}
                </>
            )}
        </>
    )
}

export const TotalResilienceActionsView = (
    insight: TotalResilienceActionsInsight
) => {
    return (
        <>
            <Box display="flex" justifyContent="center">
                <Typography
                    variant="h2"
                    color={customDarkBlueColor}
                    pb="0.25rem"
                >
                    {insight.headline}
                </Typography>
                <Box mt="-0.5rem">
                    <Popover
                        {...topAlignedPopoverPositioning}
                        icon={
                            <IoInformationCircle color={customDarkBlueColor} />
                        }
                        helpText={renderHelpText(insight)}
                    />
                </Box>
            </Box>
            <Typography
                color={customDarkBlueColor}
                fontWeight="500"
                maxWidth="27rem"
                margin="0 auto"
            >
                {insight.subline}
            </Typography>
        </>
    )
}
